import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, withRetry } from '@/lib/supabase';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { AlertCircle, Sparkles } from 'lucide-react';

interface AddProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProductAdded: () => void;
}

export function AddProductDialog({ isOpen, onClose, onProductAdded }: AddProductDialogProps) {
  const { user } = useAuth();
  const { canAddProduct, showUpgradeModal, updateActiveProductCount } = useSubscription();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setName('');
    setPrice('');
    setCategory('');
    setUrl('');
    setIsLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canAddProduct) {
      showUpgradeModal();
      return;
    }

    if (!user) return;

    if (!name || !price) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setIsLoading(true);
    try {
      const numericPrice = parseFloat(price.replace(',', '.'));
      
      // Verwende withRetry für robuste API-Aufrufe
      await withRetry(async () => {
        const { error } = await supabase
          .from('waitlist')
          .insert([
            {
              user_id: user.id,
              name,
              price: numericPrice,
              category: category || 'Sonstiges',
              url: url || null
            }
          ]);

        if (error) throw error;
        return true;
      });

      toast.success('Produkt erfolgreich hinzugefügt');
      updateActiveProductCount(); // Aktualisiere den Produktzähler
      onProductAdded();
      handleClose();
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Fehler beim Hinzufügen des Produkts. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-gradient-to-br from-white to-emerald-50/30 border-emerald-200/50">
        <DialogHeader className="text-center pb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="text-xl text-emerald-900">Neues Produkt hinzufügen</DialogTitle>
          <DialogDescription className="text-emerald-700">
            Füge ein neues Produkt zu deiner Warteliste hinzu
          </DialogDescription>
        </DialogHeader>

        {!canAddProduct ? (
          <div className="space-y-6 p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
            <div className="text-center text-amber-700">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Produktlimit erreicht</h3>
              <p className="text-sm text-amber-600">
                Du hast das Maximum an aktiven Produkten in deinem aktuellen Plan erreicht.
              </p>
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              onClick={showUpgradeModal}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Jetzt upgraden für mehr Produkte
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-emerald-800 font-medium">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. MacBook Pro 14"
                  required
                  className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price" className="text-emerald-800 font-medium">Preis (€) *</Label>
                <Input
                  id="price"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="2499,00"
                  required
                  inputMode="decimal"
                  pattern="[0-9]*[,.]?[0-9]*"
                  className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-emerald-800 font-medium">Kategorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500">
                  {category ? (
                    <div className="flex items-center gap-2">
                      {category === 'Business' && <span>💼</span>}
                      {category === 'Nice-to-Have' && <span>🎯</span>}
                      {category === 'Persönlich' && <span>🏠</span>}
                      {category === 'Sonstiges' && <span>📦</span>}
                      <span>{category}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Kategorie wählen</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Business">
                    <div className="flex items-center gap-2">
                      <span>💼</span>
                      <span>Business</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Nice-to-Have">
                    <div className="flex items-center gap-2">
                      <span>🎯</span>
                      <span>Nice-to-Have</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Persönlich">
                    <div className="flex items-center gap-2">
                      <span>🏠</span>
                      <span>Persönlich</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Sonstiges">
                    <div className="flex items-center gap-2">
                      <span>📦</span>
                      <span>Sonstiges</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="url" className="text-emerald-800 font-medium">Produkt-URL (optional)</Label>
              <Input
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                type="url"
                className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>

            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <p className="text-emerald-800 text-sm text-center">
                💡 <strong>Tipp:</strong> Nach 30 Tagen kannst du bewusst entscheiden, ob du das Produkt wirklich brauchst!
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleClose}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              >
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Wird hinzugefügt...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Zur Warteliste hinzufügen
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
} 